// @givmo/fundraisers-module — the email a message makes, framed whole in the composer's preview (FCM-WEB50,
// D-FCM-64's decision 2: "Preview showing the email itself, from the server's dry run").
//
// The frame shows the dry run's `email_html`, the server's own render of the email a named donor gets, as
// the send renders it. Nothing in it is the module's: no word is added, none is taken out.
//
// ⚠ WHY NOT `srcdoc`. Both consoles serve `style-src 'self'` (`firebase*.json`), and a `srcdoc` (or `blob:`,
// or `about:blank`) document inherits its parent's policy. An email is styled inline — its `<style>`
// blocks and `style` attributes — so under that policy it would paint as bare text: no card, no colour, no
// type (measured in headless Chromium under the consoles' own header: 26 refused inline styles, the card
// transparent). Parsing it anywhere in the page is refused the same way, and reported, so the styles are
// lifted out of the TEXT first (`splitEmailStyles`): the `<style>` rules become one stylesheet the frame
// adopts through CSSOM (a constructed sheet in the frame's own window), and each `style` attribute rides a
// data attribute until it is written through CSSOM (`style.cssText`). The CSSOM is the page's own script
// writing styles, which `style-src` does not govern, as React's own `style` props are.
//
// The frame runs no script (`sandbox` without `allow-scripts`; the email carries none), and its links are
// shown, not followed: the preview is the email, not a way out of the console. `allow-same-origin` lets the
// module write into it and size it to the email, so the email reads whole, with no box of its own to
// scroll.

import { useEffect, useRef, useState } from 'react';
import { testIdProp } from '@givmo/design-system';

/** The email's markup with its styles lifted out of the text, and the rules of its `<style>` blocks. */
export interface SplitEmail {
  markup: string;
  sheet: string;
}

/** The data attribute a `style` attribute's declarations ride until they are written through CSSOM. */
const STYLE_CARRIER = 'data-email-style';

/**
 * Take an email's styles out of its TEXT, before anything parses it. Comments go first (Outlook's
 * conditional blocks carry a `<style>` of their own that a browser never applies); then each `<style>`
 * block's rules are collected; then each `style` attribute is renamed to `data-email-style`, its value
 * kept for the parser to decode. The server's template escapes every quote in the words it carries, so a
 * quoted `style=` can only be an attribute.
 */
export function splitEmailStyles(html: string): SplitEmail {
  const rules: string[] = [];
  const markup = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi, (_block, css: string) => {
      rules.push(css);
      return '';
    })
    .replace(/(\s)style(\s*=\s*["'])/gi, `$1${STYLE_CARRIER}$2`);
  return { markup, sheet: rules.join('\n') };
}

/**
 * Take the email's dark-mode rules (`@media (prefers-color-scheme: dark)`) out of a sheet, at any depth.
 * The preview is the light email the ruled frames draw: the console is light, and a frame following a
 * dark OS drew the email's dark variant inside it. Pinning the frame's `color-scheme` is not enough (only
 * Firefox lets it decide the frame's `prefers-color-scheme`), so the rules go.
 */
function dropDarkScheme(sheet: CSSStyleSheet | CSSGroupingRule): void {
  for (let index = sheet.cssRules.length - 1; index >= 0; index -= 1) {
    const rule = sheet.cssRules[index]!;
    const media = 'media' in rule ? (rule as CSSMediaRule).media.mediaText : '';
    if (/prefers-color-scheme\s*:\s*dark/i.test(media)) sheet.deleteRule(index);
    else if ('cssRules' in rule) dropDarkScheme(rule as CSSGroupingRule);
  }
}

/** Draw the email into the frame's own document (idempotent: each call replaces what the last drew). */
function drawEmail(frame: HTMLIFrameElement, html: string): void {
  const doc = frame.contentDocument;
  const win = frame.contentWindow as (Window & typeof globalThis) | null;
  if (doc === null || win === null) return;
  const { markup, sheet } = splitEmailStyles(html);
  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  parsed.querySelectorAll('script, link, meta, base, title').forEach((node) => node.remove());
  // A link opens nothing here, but says where it goes on hover (the KCC hand edit, 2026-11-01): its web
  // address whole, an email address or a phone number without its scheme. None for `#` or a title of its own.
  parsed.querySelectorAll('a[href]').forEach((link) => {
    const href = (link.getAttribute('href') ?? '').trim();
    if (!link.hasAttribute('title') && href !== '' && !href.startsWith('#')) {
      link.setAttribute('title', href.replace(/^(mailto|tel):/i, ''));
    }
    link.removeAttribute('href');
  });
  const body = doc.importNode(parsed.body, true);
  if (parsed.documentElement.lang) doc.documentElement.lang = parsed.documentElement.lang;
  if (doc.body) doc.body.replaceWith(body);
  else doc.documentElement.append(body);
  for (const element of [body, ...body.querySelectorAll<HTMLElement>(`[${STYLE_CARRIER}]`)]) {
    const declarations = element.getAttribute(STYLE_CARRIER);
    if (declarations === null) continue;
    element.removeAttribute(STYLE_CARRIER);
    element.style.cssText = declarations;
  }
  if (typeof win.CSSStyleSheet === 'function' && 'adoptedStyleSheets' in doc) {
    const constructed = new win.CSSStyleSheet();
    if (typeof constructed.replaceSync === 'function') {
      constructed.replaceSync(sheet);
      dropDarkScheme(constructed);
      doc.adoptedStyleSheets = [constructed];
      return;
    }
  }
  // A browser without constructed stylesheets: a style element (refused under the consoles' policy, where
  // the inline declarations above still paint the email).
  const element = doc.createElement('style');
  element.textContent = sheet;
  doc.head.replaceChildren(element);
  if (element.sheet !== null) dropDarkScheme(element.sheet);
}

/**
 * Where the footer's first line sits, in pixels from the top of the frame's document, or null. The server
 * adds its footer after the body, always, and its last link (the privacy policy, or a sample's Unsubscribe)
 * is the footer's: the cell holding that link is the footer, below its rule and padding.
 */
export function footerTopIn(doc: Document | null | undefined): number | null {
  const links = doc?.body?.querySelectorAll('a');
  const last = links && links.length > 0 ? links[links.length - 1]! : null;
  const cell = last?.closest('td') ?? null;
  if (cell === null || doc?.defaultView == null) return null;
  const padding = Number.parseFloat(doc.defaultView.getComputedStyle(cell).paddingTop) || 0;
  return Math.round(cell.getBoundingClientRect().top + padding);
}

/**
 * The server's render of the email, framed whole: its own width, its height the email's. `title` names the
 * frame for a screen reader (the email's subject, as the server wrote it). `onFooterTop` hears where the
 * footer's first line sits, for a label beside it (the email page's "Always included"). `decorate` draws over
 * the drawn email in the frame's own document (the email designer's wash and its footer chip, EMC-WEB10); it
 * runs after every draw, so it must be idempotent over a fresh draw.
 */
export function MessageEmailFrame({
  html,
  title,
  onFooterTop,
  decorate,
  testId = 'fundraiser-message-preview-frame',
  className = 'block w-full rounded-lg border border-border-subtle bg-surface',
  onHeight,
}: {
  html: string;
  title: string;
  onFooterTop?: (top: number | null) => void;
  decorate?: (doc: Document) => void;
  testId?: string;
  className?: string;
  /** The frame's height as it follows the email's, for a host that scales the email. */
  onHeight?: (height: number) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(640);
  const footerHeard = useRef(onFooterTop);
  footerHeard.current = onFooterTop;
  const reportHeight = useRef(onHeight);
  reportHeight.current = onHeight;
  useEffect(() => {
    reportHeight.current?.(height);
  }, [height]);
  useEffect(() => {
    const node = frame.current;
    if (node === null) return;
    // The email's own height: the bottom of its last block. The document's scroll height is never less
    // than the frame itself, so measuring that would only ever grow the frame; the frame's border sits
    // outside its document, so it adds to the height.
    const fit = () => {
      const body = node.contentDocument?.body;
      if (!body) return;
      const bottom = Math.max(
        0,
        ...Array.from(body.children, (child) => child.getBoundingClientRect().bottom),
      );
      if (bottom > 0) setHeight(Math.ceil(bottom) + (node.offsetHeight - node.clientHeight));
      if (footerHeard.current) {
        const top = footerTopIn(node.contentDocument);
        // The frame's top border sits outside its document.
        footerHeard.current(top === null ? null : top + node.clientTop);
      }
    };
    const draw = () => {
      drawEmail(node, html);
      if (decorate !== undefined && node.contentDocument !== null) decorate(node.contentDocument);
      // An image that lands later (the wordmark) may move the email's last block.
      node.contentDocument?.addEventListener('load', fit, true);
      fit();
    };
    draw();
    // A browser that replaces the frame's first empty document with another fires `load`: draw again.
    node.addEventListener('load', draw);
    // A narrower frame wraps the email's lines again, so its height follows the frame's width.
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    resized?.observe(node);
    return () => {
      node.removeEventListener('load', draw);
      node.contentDocument?.removeEventListener('load', fit, true);
      resized?.disconnect();
    };
  }, [html, decorate]);
  return (
    <iframe
      ref={frame}
      title={title}
      sandbox="allow-same-origin"
      className={className}
      style={{ height }}
      {...testIdProp(testId)}
    />
  );
}
